import React, { useState, useMemo, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Input, Select } from "../../../../_metronic/_partials/controls";
import TextEditor from "../../../../_zebra/_partials/TextEditor";
import { CourseListSelection } from "../../SelectionWidgets/CourseListSelection";

const BranchCourseCustomizationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  branch_course_id: Yup.array().min(1, "Branch Course is required"),
  message_type: Yup.string().required("Message Type is required"),
  message_details: Yup.string()
    .test(
      "rich-text-required",
      "Message Details are required",
      (value) => !!value && value.replace(/<[^>]*>/g, "").trim().length > 0
    ),
});

const mapCourseForWidget = (course) => ({
  ...course,
  course_id: course.branch_course_id,
});

export function BranchCourseCustomizationForm({
  values,
  btnRef,
  onSubmit,
  branchCourses,
  programTypes = [],
  isEdit = false,
}) {
  const [courseTypeFilter, setCourseTypeFilter] = useState("");
  const [programTypeFilter, setProgramTypeFilter] = useState("");
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [coursesToAdd, setCoursesToAdd] = useState([]);
  const [coursesToRemove, setCoursesToRemove] = useState([]);

  const activeCourses = useMemo(
    () => (branchCourses || []).filter((c) => c.active_id == 1),
    [branchCourses]
  );

  useEffect(() => {
    if (!activeCourses.length) return;
    const selectedIds = new Set(
      (values.branch_course_id || []).map((item) => parseInt(item.value, 10))
    );
    setSelectedCourses(
      activeCourses
        .filter((c) => selectedIds.has(c.branch_course_id))
        .map(mapCourseForWidget)
    );
    setCoursesToAdd([]);
    setCoursesToRemove([]);
  }, [values.branch_course_id, activeCourses]);

  const allAvailableCourses = useMemo(() => {
    const selectedIds = new Set(selectedCourses.map((c) => c.branch_course_id));
    return activeCourses
      .filter((c) => !selectedIds.has(c.branch_course_id))
      .map(mapCourseForWidget);
  }, [activeCourses, selectedCourses]);

  const filteredAvailableCourses = useMemo(() => {
    let result = allAvailableCourses;
    if (courseTypeFilter === "RC")
      result = result.filter((c) => c.frequency_unit === "monthly");
    else if (courseTypeFilter === "Camp")
      result = result.filter((c) => c.frequency_unit !== "monthly");
    if (programTypeFilter)
      result = result.filter(
        (c) => c.program_name && c.program_name.includes(programTypeFilter)
      );
    return result;
  }, [allAvailableCourses, courseTypeFilter, programTypeFilter]);

  return (
    <Formik
      enableReinitialize={true}
      initialValues={values}
      validationSchema={BranchCourseCustomizationSchema}
      onSubmit={(formValues) => {
        onSubmit({
          ...formValues,
          branch_course_id: formValues.branch_course_id.map((item) =>
            parseInt(item.value, 10)
          ),
        });
      }}
    >
      {({ handleSubmit, values: formValues, errors, touched, setFieldValue }) => {
        const handleSetSelectedCourses = (newCourses) => {
          setSelectedCourses(newCourses);
          setFieldValue(
            "branch_course_id",
            newCourses.map((c) => ({ value: c.branch_course_id.toString() }))
          );
        };

        return (
          <Form className="form form-label-right">
            <div className="form-group row">
              <div className="col-lg-6">
                <label className="required-field">Name</label>
                <Field
                  name="name"
                  component={Input}
                  placeholder="Enter name"
                  type="text"
                />
                {touched.name && errors.name && (
                  <ErrorMessage
                    name="name"
                    component="div"
                    className="invalid-feedback"
                  />
                )}
              </div>
            </div>

            <div className="form-group row">
              <div className="col-lg-3">
                <label>Batch Type</label>
                <select
                  className="form-control"
                  value={courseTypeFilter}
                  onChange={(e) => setCourseTypeFilter(e.target.value)}
                >
                  <option value="">All</option>
                  <option value="RC">Regular Classes</option>
                  <option value="Camp">Camp</option>
                </select>
              </div>
              <div className="col-lg-3">
                <label>Program Type</label>
                <select
                  className="form-control"
                  value={programTypeFilter}
                  onChange={(e) => setProgramTypeFilter(e.target.value)}
                >
                  <option value="">All</option>
                  {programTypes.map((p) => (
                    <option key={p.id} value={p.value}>
                      {p.value}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-lg-3">
                <Select name="message_type" label="Message Type">
                  <option value="description">Description</option>
                  <option value="schedule">Schedule</option>
                </Select>
              </div>
              {isEdit && (
                <div className="col-lg-3">
                  <label>Status</label>
                  <div className="d-flex align-items-center mt-2">
                    <span
                      className={`label label-lg label-inline mr-3 ${
                        formValues.active_id == 1
                          ? "label-light-success"
                          : "label-light-danger"
                      }`}
                    >
                      {formValues.active_id == 1 ? "Active" : "Inactive"}
                    </span>
                    <label className="checkbox checkbox-lg mb-0">
                      <input
                        type="checkbox"
                        checked={formValues.active_id == 1}
                        onChange={(e) =>
                          setFieldValue("active_id", e.target.checked ? 1 : 0)
                        }
                      />
                      <span></span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            <div className="form-group row">
              <div className="col-lg-12">
                <label className="required-field">Branch Course</label>
                {touched.branch_course_id && errors.branch_course_id && (
                  <div className="invalid-feedback d-block mb-2">
                    {errors.branch_course_id}
                  </div>
                )}
                <CourseListSelection
                  type="batch"
                  batchCourses={selectedCourses}
                  setBatchCourses={handleSetSelectedCourses}
                  batchMissingCourses={filteredAvailableCourses}
                  setBatchMissingCourses={() => {}}
                  coursesToAdd={coursesToAdd}
                  setCoursesToAdd={setCoursesToAdd}
                  coursesToRemove={coursesToRemove}
                  setCoursesToRemove={setCoursesToRemove}
                  showToggle={false}
                  maxHeight="200px"
                />
              </div>
            </div>

            <div className="form-group row">
              <div className="col-lg-10">
                <label className="required-field">Message Details</label>
                <TextEditor
                  name="message_details"
                  value={formValues.message_details}
                  setValue={setFieldValue}
                  setPlainTextBody={() => {}}
                />
                {touched.message_details && errors.message_details && (
                  <div className="invalid-feedback d-block">
                    {errors.message_details}
                  </div>
                )}
              </div>
            </div>


            <button
              type="submit"
              style={{ display: "none" }}
              ref={btnRef}
              onSubmit={() => handleSubmit()}
            />
          </Form>
        );
      }}
    </Formik>
  );
}
